import React, { useState, useEffect } from 'react';
import { ShoppingBag, Package, Tag, Settings, ShoppingCart, Plus, Edit2, Trash2, Image as ImageIcon, Link, Copy, Check, Loader2, X, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { getWebShopSettings, createWebShopSettings, updateWebShopSettings } from '../../../redux/features/webshopSlice';
import * as api from '../../../redux/api';
import { getCurrentPackage, getMaxProducts, canAddProduct } from '../../../utils/packageLimits';
import { su } from '../../inventory/stockUi';

const WebShopView = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [refreshOrders, setRefreshOrders] = useState(0);

  const tabs = [
    { id: 'products', label: 'Proizvodi', icon: Package },
    { id: 'categories', label: 'Kategorije', icon: Tag },
    { id: 'orders', label: 'Narudžbe', icon: ShoppingCart },
    { id: 'settings', label: 'Postavke', icon: Settings }
  ];

  return (
    <div className={`${su.card} ${su.cardPad}`}>
      <div className="flex items-center gap-2 mb-3">
        <ShoppingBag className="w-5 h-5 text-blue-400" />
        <h2 className="text-lg font-semibold text-white">Web Shop</h2>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-700">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                // Refresh orders kada se otvori OrdersTab
                if (tab.id === 'orders') {
                  setRefreshOrders(prev => prev + 1);
                }
              }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'categories' && <CategoriesTab />}
        {activeTab === 'orders' && <OrdersTab key={refreshOrders} />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
};

// Products Tab Component
const ProductsTab = () => {
  const { user } = useSelector((state) => state.auth);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    deviceCategoryId: '',
    quantity: '',
    images: [],
    sku: '',
    active: true,
    specifications: []
  });

  // Load products from API on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.getWebShopProducts();
        setProducts(response.data);
      } catch (error) {
        console.error('Error loading products:', error);
        toast.error(error.response?.data?.message || 'Greška pri učitavanju proizvoda');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();

    // Load categories from DeviceCategory API (jedinstvena lista kategorija uređaja)
    const loadCategories = async () => {
      try {
        const response = await api.fetchDeviceCategories();
        setCategories(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Naziv proizvoda je obavezan');
      return;
    }

    if (!formData.sku.trim()) {
      toast.error('SKU je obavezan');
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Cena mora biti veća od 0');
      return;
    }

    if (!formData.quantity || parseInt(formData.quantity) < 0) {
      toast.error('Količina mora biti 0 ili veća');
      return;
    }

    try {
      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        category: formData.category || '',
        deviceCategoryId: formData.deviceCategoryId || '',
        quantity: parseInt(formData.quantity) || 0,
        images: formData.images.filter(img => img.trim() !== ''),
        sku: formData.sku.trim(),
        active: formData.active,
        specifications: formData.specifications.filter(spec => spec.key.trim() !== '' && spec.value.trim() !== '')
      };

      let response;
      if (editingProduct) {
        // Update existing product
        response = await api.updateWebShopProduct(editingProduct._id, productData);
        setProducts(products.map(prod => prod._id === editingProduct._id ? response.data : prod));
        toast.success('Proizvod je uspešno ažuriran');
      } else {
        // Create new product
        response = await api.createWebShopProduct(productData);
        setProducts([...products, response.data]);
        toast.success('Proizvod je uspešno dodat');
      }

      handleCancelEdit();
    } catch (error) {
      toast.error(error.response?.data?.message || (editingProduct ? 'Greška pri ažuriranju proizvoda' : 'Greška pri dodavanju proizvoda'));
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      category: product.category || '',
      deviceCategoryId:
        product.deviceCategory?._id ||
        product.deviceCategory?.id ||
        product.deviceCategory ||
        '',
      quantity: product.quantity || '',
      images: product.images && product.images.length > 0 ? product.images : (product.image ? [product.image] : []),
      sku: product.sku || '',
      active: product.active !== undefined ? product.active : true,
      specifications: product.specifications && product.specifications.length > 0 
        ? product.specifications 
        : []
    });
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      deviceCategoryId: '',
      quantity: '',
      images: [],
      sku: '',
      active: true,
      specifications: []
    });
    setShowForm(false);
  };

  const handleAddImage = () => {
    setFormData({
      ...formData,
      images: [...formData.images, '']
    });
  };

  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({
      ...formData,
      images: newImages
    });
  };

  const handleFileUpload = (index, file) => {
    if (!file) return;

    // Proveri tip fajla
    if (!file.type.startsWith('image/')) {
      toast.error('Molimo izaberite sliku');
      return;
    }

    // Proveri veličinu (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      const newImages = [...formData.images];
      newImages[index] = base64String;
      setFormData({
        ...formData,
        images: newImages
      });
    };
    reader.onerror = () => {
      toast.error('Greška pri čitanju fajla');
    };
    reader.readAsDataURL(file);
  };

  const handleAddImageWithFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Za mobilne uređaje - kamera
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        const newIndex = formData.images.length;
        setFormData({
          ...formData,
          images: [...formData.images, '']
        });
        // Sačekaj da se state ažurira, pa upload-uj
        setTimeout(() => {
          handleFileUpload(newIndex, file);
        }, 0);
      }
    };
    input.click();
  };

  const handleAddSpecification = () => {
    setFormData({
      ...formData,
      specifications: [...formData.specifications, { key: '', value: '' }]
    });
  };

  const handleRemoveSpecification = (index) => {
    setFormData({
      ...formData,
      specifications: formData.specifications.filter((_, i) => i !== index)
    });
  };

  const handleSpecificationChange = (index, field, value) => {
    const newSpecs = [...formData.specifications];
    newSpecs[index] = { ...newSpecs[index], [field]: value };
    setFormData({
      ...formData,
      specifications: newSpecs
    });
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj proizvod?')) {
      return;
    }

    try {
      await api.deleteWebShopProduct(productId);
      setProducts(products.filter(prod => prod._id !== productId));
      toast.success('Proizvod je uspešno obrisan');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri brisanju proizvoda');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Proizvodi</h3>
          {user && (() => {
            const currentPackage = getCurrentPackage(user);
            const maxProducts = getMaxProducts(user);
            const canAdd = canAddProduct(user, products.length);
            if (maxProducts !== Infinity) {
              return (
                <p className="text-sm text-gray-400 mt-1">
                  {products.length} / {maxProducts} proizvoda
                  {!canAdd && <span className="text-red-400 ml-2">(Limit dostignut)</span>}
                </p>
              );
            }
            return null;
          })()}
        </div>
        {user && (() => {
          const canAdd = canAddProduct(user, products.length);
          return (
            <button
              onClick={() => {
                if (editingProduct) {
                  handleCancelEdit();
                } else {
                  if (!canAdd) {
                    toast.error(`Dostigli ste limit proizvoda za vaš paket. Maksimalno dozvoljeno: ${getMaxProducts(user)}. Nadogradite paket za više proizvoda.`);
                    return;
                  }
                  setShowForm(!showForm);
                }
              }}
              disabled={!canAdd && !editingProduct}
              className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md transition ${
                canAdd || editingProduct
                  ? 'bg-blue-600 text-white hover:bg-blue-500'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
              }`}
              title={!canAdd && !editingProduct ? `Dostigli ste limit proizvoda za vaš paket. Maksimalno dozvoljeno: ${getMaxProducts(user)}.` : ''}
            >
              <Plus className="w-4 h-4" />
              {editingProduct ? 'Otkaži izmene' : 'Dodaj proizvod'}
            </button>
          );
        })()}
      </div>

      {showForm && (
        <div className="bg-gray-700 rounded-lg p-4 mb-4">
          <h4 className="text-md font-semibold text-white mb-4">
            {editingProduct ? 'Izmena proizvoda' : 'Novi proizvod'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Naziv proizvoda *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  SKU / Šifra *
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Cena *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Količina na stanju *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Kategorija
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Izaberi kategoriju</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {/* Images Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Slike proizvoda
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAddImageWithFile}
                    className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    Iz galerije
                  </button>
                  <button
                    type="button"
                    onClick={handleAddImage}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    URL
                  </button>
                </div>
              </div>
              {formData.images.length === 0 ? (
                <p className="text-sm text-gray-400 mb-2">Nema dodatih slika. Kliknite na "Iz galerije" ili "URL" da dodate.</p>
              ) : (
                <div className="space-y-2">
                  {formData.images.map((image, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={image}
                        onChange={(e) => handleImageChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="URL slike ili base64..."
                      />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`file-upload-${index}`}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(index, file);
                          }
                        }}
                      />
                      <label
                        htmlFor={`file-upload-${index}`}
                        className="px-2 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors cursor-pointer"
                        title="Izmeni sliku iz galerije"
                      >
                        <ImageIcon className="w-4 h-4" />
                      </label>
                      {image && (
                        <img 
                          src={image} 
                          alt={`Preview ${index + 1}`}
                          className="w-16 h-16 object-cover rounded border border-gray-600"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="px-2 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                        title="Ukloni sliku"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Specifications Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Karakteristike
                </label>
                <button
                  type="button"
                  onClick={handleAddSpecification}
                  className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Dodaj karakteristiku
                </button>
              </div>
              {formData.specifications.length === 0 ? (
                <p className="text-sm text-gray-400 mb-2">Nema dodatih karakteristika. Primer: boja:bela, centrifuga:1000rpm</p>
              ) : (
                <div className="space-y-2">
                  {formData.specifications.map((spec, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={spec.key}
                        onChange={(e) => handleSpecificationChange(index, 'key', e.target.value)}
                        className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Karakteristika (npr. boja)"
                      />
                      <span className="text-gray-400">:</span>
                      <input
                        type="text"
                        value={spec.value}
                        onChange={(e) => handleSpecificationChange(index, 'value', e.target.value)}
                        className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Opis (npr. bela)"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveSpecification(index)}
                        className="px-2 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Napomena
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Dodatne napomene o proizvodu..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 transition-colors"
              >
                Otkaži
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {editingProduct ? 'Sačuvaj izmene' : 'Sačuvaj proizvod'}
              </button>
            </div>
          </form>
        </div>
      )}

      {products.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Nema proizvoda. Dodajte prvi proizvod da biste počeli.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-800">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Slika</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Naziv</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">SKU</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Cena</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Količina</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-300 uppercase">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                    Učitavanje proizvoda...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                    Nema proizvoda. Dodajte prvi proizvod.
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const displayImage = (product.images && product.images.length > 0) 
                    ? product.images[0] 
                    : (product.image || '');
                  
                  return (
                    <tr key={product._id} className="hover:bg-gray-700">
                      <td className="px-4 py-2">
                        {displayImage ? (
                          <img 
                            src={displayImage} 
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded border border-gray-600"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-12 h-12 bg-gray-600 rounded border border-gray-600 flex items-center justify-center image-placeholder" 
                          style={{ display: displayImage ? 'none' : 'flex' }}
                        >
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      </td>
                      <td className="px-4 py-2 text-white">{product.name}</td>
                      <td className="px-4 py-2 text-gray-300">{product.sku}</td>
                      <td className="px-4 py-2 text-gray-300">{product.price} RSD</td>
                      <td className="px-4 py-2 text-gray-300">{product.quantity}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${product.active ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'}`}>
                          {product.active ? 'Aktivan' : 'Neaktivan'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex justify-center gap-2">
                          <button 
                            onClick={() => handleEditProduct(product)}
                            className="text-blue-400 hover:text-blue-300"
                            title="Izmeni proizvod"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteProduct(product._id)}
                            className="text-red-400 hover:text-red-300"
                            title="Obriši proizvod"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Categories Tab Component
const CategoriesTab = () => {
  const [showForm, setShowForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: ''
  });

  // Load categories from API on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await api.getWebShopCategories();
        setCategories(response.data);
      } catch (error) {
        console.error('Error loading categories:', error);
        toast.error(error.response?.data?.message || 'Greška pri učitavanju kategorija');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Naziv kategorije je obavezan');
      return;
    }

    try {
      const response = await api.createWebShopCategory({
        name: formData.name.trim(),
        description: formData.description.trim() || '',
        image: formData.image.trim() || ''
      });

      setCategories([...categories, response.data]);
      toast.success('Kategorija je uspešno dodata');
      setFormData({ name: '', description: '', image: '' });
      setShowForm(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri dodavanju kategorije');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu kategoriju?')) {
      return;
    }

    try {
      await api.deleteWebShopCategory(categoryId);
      setCategories(categories.filter(cat => cat._id !== categoryId));
      toast.success('Kategorija je uspešno obrisana');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri brisanju kategorije');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Kategorije proizvoda</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Dodaj kategoriju
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-700 rounded-lg p-4 mb-4">
          <h4 className="text-md font-semibold text-white mb-4">Nova kategorija</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Naziv kategorije *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Opis
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                URL slike kategorije
              </label>
              <input
                type="url"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/category-image.jpg"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 transition-colors"
              >
                Otkaži
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Sačuvaj kategoriju
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin" />
          <p>Učitavanje kategorija...</p>
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Tag className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Nema kategorija. Dodajte prvu kategoriju da biste počeli.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div key={category._id} className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors">
              <h4 className="text-lg font-semibold text-white mb-2">{category.name}</h4>
              <p className="text-sm text-gray-400 mb-4">{category.description || 'Nema opisa'}</p>
              <div className="flex justify-end gap-2">
                <button className="text-blue-400 hover:text-blue-300">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(category._id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Orders Tab Component
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.getWebShopOrders();
      setOrders(response.data);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error(error.response?.data?.message || 'Greška pri učitavanju narudžbina');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrders();
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.updateWebShopOrderStatus(orderId, newStatus);
      setOrders(orders.map(order => 
        order._id === orderId ? { ...order, status: newStatus } : order
      ));
      // Ažuriraj i selectedOrder ako je otvoren
      if (selectedOrder && selectedOrder._id === orderId) {
        setOrderDetails(prev => prev ? { ...prev, status: newStatus } : null);
      }
      toast.success('Status narudžbine je ažuriran');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri ažuriranju statusa');
    }
  };

  const handleViewDetails = async (order) => {
    setSelectedOrder(order);
    setLoadingDetails(true);
    try {
      const response = await api.getWebShopOrder(order._id);
      setOrderDetails(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error(error.response?.data?.message || 'Greška pri učitavanju detalja narudžbine');
      setOrderDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Narudžbe</h3>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Osveži listu narudžbina"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Osveži
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin" />
          <p>Učitavanje narudžbina...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Nema narudžbi.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-800">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Broj narudžbe</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Kupac</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Datum</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ukupno</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {orders.map((order) => {
                // Prikaži customer podatke (iz customerId ili guestCustomer)
                const customerName = order.customerId?.firstName && order.customerId?.lastName
                  ? `${order.customerId.firstName} ${order.customerId.lastName}`
                  : (order.guestCustomer?.firstName && order.guestCustomer?.lastName
                    ? `${order.guestCustomer.firstName} ${order.guestCustomer.lastName}`
                    : 'Nepoznat kupac');
                
                return (
                  <tr key={order._id} className="hover:bg-gray-700">
                    <td className="px-4 py-2 text-white">#{order.orderNumber}</td>
                    <td className="px-4 py-2 text-gray-300">
                      {customerName}
                    </td>
                  <td className="px-4 py-2 text-gray-300">
                    {new Date(order.createdAt).toLocaleDateString('sr-RS')}
                  </td>
                  <td className="px-4 py-2">
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order._id, e.target.value)}
                      className="px-2 py-1 rounded text-xs bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Na čekanju</option>
                      <option value="processing">U obradi</option>
                      <option value="shipped">Poslato</option>
                      <option value="delivered">Isporučeno</option>
                      <option value="cancelled">Otkazano</option>
                    </select>
                  </td>
                  <td className="px-4 py-2 text-gray-300">{order.total.toFixed(2)} RSD</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleViewDetails(order)}
                      className="text-blue-400 hover:text-blue-300 text-sm"
                    >
                      Detalji
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-white">
                Detalji narudžbine #{selectedOrder.orderNumber}
              </h2>
              <button
                onClick={() => {
                  setSelectedOrder(null);
                  setOrderDetails(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {loadingDetails ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                  <p className="text-gray-400">Učitavanje detalja...</p>
                </div>
              ) : orderDetails ? (
                <div className="space-y-6">
                  {/* Order Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-3">Informacije o narudžbini</h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-400">Broj narudžbine:</span>
                          <p className="text-white font-medium">#{orderDetails.orderNumber}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Datum:</span>
                          <p className="text-white">
                            {new Date(orderDetails.createdAt).toLocaleString('sr-RS')}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Status:</span>
                          <select
                            value={orderDetails.status}
                            onChange={(e) => handleStatusChange(orderDetails._id, e.target.value)}
                            className="ml-2 px-2 py-1 rounded text-xs bg-gray-600 text-white border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="pending">Na čekanju</option>
                            <option value="processing">U obradi</option>
                            <option value="shipped">Poslato</option>
                            <option value="delivered">Isporučeno</option>
                            <option value="cancelled">Otkazano</option>
                          </select>
                        </div>
                        <div>
                          <span className="text-gray-400">Način plaćanja:</span>
                          <p className="text-white capitalize">
                            {orderDetails.paymentMethod === 'cash_on_delivery' ? 'Pouzećem' : orderDetails.paymentMethod}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-3">Cene</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Međusobno:</span>
                          <span className="text-white">{orderDetails.subtotal.toFixed(2)} RSD</span>
                        </div>
                        {orderDetails.tax > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Porez:</span>
                            <span className="text-white">{orderDetails.tax.toFixed(2)} RSD</span>
                          </div>
                        )}
                        {orderDetails.shipping > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Dostava:</span>
                            <span className="text-white">{orderDetails.shipping.toFixed(2)} RSD</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-gray-600 pt-2 mt-2">
                          <span className="text-white font-semibold">Ukupno:</span>
                          <span className="text-blue-400 font-bold text-lg">
                            {orderDetails.total.toFixed(2)} RSD
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Podaci o kupcu</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Ime i prezime:</span>
                        <p className="text-white font-medium">
                          {orderDetails.customerId?.firstName || ''} {orderDetails.customerId?.lastName || ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-400">Email:</span>
                        <p className="text-white">{orderDetails.customerId?.email || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Telefon:</span>
                        <p className="text-white">{orderDetails.customerId?.phone || orderDetails.shippingAddress?.phone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  {orderDetails.shippingAddress && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-3">Adresa dostave</h3>
                      <div className="text-sm text-white">
                        <p className="mb-1">
                          {orderDetails.shippingAddress.street}
                        </p>
                        <p className="mb-1">
                          {orderDetails.shippingAddress.city}
                          {orderDetails.shippingAddress.postalCode && `, ${orderDetails.shippingAddress.postalCode}`}
                        </p>
                        {orderDetails.shippingAddress.country && (
                          <p>{orderDetails.shippingAddress.country}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Proizvodi u narudžbini</h3>
                    <div className="space-y-3">
                      {orderDetails.items && orderDetails.items.length > 0 ? (
                        orderDetails.items.map((item, index) => (
                          <div key={index} className="flex items-center gap-4 bg-gray-600 p-3 rounded-lg">
                            {item.productId?.image && (
                              <img 
                                src={item.productId.image} 
                                alt={item.name}
                                className="w-16 h-16 object-cover rounded border border-gray-500"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div className="flex-1">
                              <p className="text-white font-medium">{item.name}</p>
                              {item.productId?.sku && (
                                <p className="text-gray-400 text-xs">SKU: {item.productId.sku}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-white">Količina: {item.quantity}</p>
                              <p className="text-blue-400 font-medium">
                                {item.price.toFixed(2)} RSD × {item.quantity} = {(item.price * item.quantity).toFixed(2)} RSD
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-400">Nema proizvoda</p>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {orderDetails.notes && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Napomena</h3>
                      <p className="text-gray-300 whitespace-pre-wrap">{orderDetails.notes}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setSelectedOrder(null);
                        setOrderDetails(null);
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                    >
                      Zatvori
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">Greška pri učitavanju detalja narudžbine</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Tab Component
const SettingsTab = () => {
  const dispatch = useDispatch();
  const { settings: webshopSettings, loading } = useSelector((state) => state.webshop || { settings: null, loading: false });
  const [copied, setCopied] = useState(false);
  
  const [settings, setSettings] = useState({
    shopName: '',
    shopDescription: '',
    shopEmail: '',
    shopPhone: '',
    currency: 'RSD',
    taxRate: 20,
    shippingEnabled: false,
    paymentMethods: []
  });

  // Load settings from API on mount
  useEffect(() => {
    dispatch(getWebShopSettings());
  }, [dispatch]);

  // Update local state when settings are loaded from API
  useEffect(() => {
    if (webshopSettings) {
      setSettings({
        shopName: webshopSettings.shopName || '',
        shopDescription: webshopSettings.shopDescription || '',
        shopEmail: webshopSettings.shopEmail || '',
        shopPhone: webshopSettings.shopPhone || '',
        currency: webshopSettings.currency || 'RSD',
        taxRate: webshopSettings.taxRate || 20,
        shippingEnabled: webshopSettings.shippingEnabled || false,
        paymentMethods: webshopSettings.paymentMethods || []
      });
    }
  }, [webshopSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!settings.shopName.trim()) {
      toast.error('Naziv shopa je obavezan');
      return;
    }

    if (!settings.shopEmail.trim()) {
      toast.error('Email je obavezan');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(settings.shopEmail)) {
      toast.error('Unesite validnu email adresu');
      return;
    }

    try {
      if (webshopSettings) {
        // Update existing settings
        await dispatch(updateWebShopSettings(settings)).unwrap();
      } else {
        // Create new settings
        await dispatch(createWebShopSettings(settings)).unwrap();
      }
      // Toast success is already shown in the slice
    } catch (error) {
      // Error toast is already shown in the slice
      console.error('Error saving web shop settings:', error);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-4">Postavke Web Shopa</h3>
      
      {/* Display shop slug if it exists */}
      {webshopSettings?.shopSlug && (
        <div className="mb-4 p-3 bg-gray-700 rounded-md">
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
            <Link className="w-4 h-4" />
            <span>Link vašeg web shopa:</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 text-blue-400 font-mono text-sm break-all">
              {window.location.origin}/{webshopSettings.shopSlug}
            </div>
            <button
              onClick={async () => {
                const link = `${window.location.origin}/${webshopSettings.shopSlug}`;
                try {
                  await navigator.clipboard.writeText(link);
                  setCopied(true);
                  toast.success('Link je kopiran');
                  setTimeout(() => setCopied(false), 2000);
                } catch (err) {
                  // Fallback za starije browsere
                  const textArea = document.createElement('textarea');
                  textArea.value = link;
                  textArea.style.position = 'fixed';
                  textArea.style.opacity = '0';
                  document.body.appendChild(textArea);
                  textArea.select();
                  try {
                    document.execCommand('copy');
                    setCopied(true);
                    toast.success('Link je kopiran');
                    setTimeout(() => setCopied(false), 2000);
                  } catch (fallbackErr) {
                    toast.error('Greška pri kopiranju linka');
                  }
                  document.body.removeChild(textArea);
                }
              }}
              className="p-2 bg-gray-600 hover:bg-gray-500 rounded-md transition-colors flex-shrink-0"
              title="Kopiraj link"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4 text-gray-300" />
              )}
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-400">
            Ovaj link možete podeliti sa potrošačima. Kada korisnici otvore ovaj link, videće stranicu sa naslovom "Dobro došli na {webshopSettings.shopName}".
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Naziv shopa *
            </label>
            <input
              type="text"
              value={settings.shopName}
              onChange={(e) => setSettings({ ...settings, shopName: e.target.value })}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={settings.shopEmail}
              onChange={(e) => setSettings({ ...settings, shopEmail: e.target.value })}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              value={settings.shopPhone}
              onChange={(e) => setSettings({ ...settings, shopPhone: e.target.value })}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Valuta
            </label>
            <select
              value={settings.currency}
              onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="RSD">RSD (Dinar)</option>
              <option value="EUR">EUR (Euro)</option>
              <option value="USD">USD (Dolar)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Porez (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={settings.taxRate}
              onChange={(e) => setSettings({ ...settings, taxRate: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Opis shopa
          </label>
          <textarea
            value={settings.shopDescription}
            onChange={(e) => setSettings({ ...settings, shopDescription: e.target.value })}
            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="shippingEnabled"
            checked={settings.shippingEnabled}
            onChange={(e) => setSettings({ ...settings, shippingEnabled: e.target.checked })}
            className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded focus:ring-blue-500"
          />
          <label htmlFor="shippingEnabled" className="text-sm text-gray-300">
            Omogući dostavu
          </label>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Čuvanje...' : 'Sačuvaj postavke'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WebShopView;

